import styled from "styled-components";
import { useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import ModalItensNf from "./ModalItensNf";

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 18px;
    color: #333;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px 16px;
    margin-bottom: 18px;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: #94a3b8;
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: #1f2937;
    font-weight: 500;
`;

const SectionTitle = styled.h3`
    margin: 18px 0 8px 0;
    font-size: 14px;
    color: #334155;
    font-weight: 700;
`;

const Hint = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: #94a3b8;
    margin-left: 8px;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-height: 28dvh;
    overflow: auto;
    border: 1px solid #ddd;
    border-radius: 8px;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 12px;
    font-size: 14px;
    color: #666;
`;

const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const Coluna = styled.td`
    text-align: ${({ align }) => align || "left"};
`;

const TotalRow = styled.tr`
    background-color: #f8fafc;
    font-weight: 700;

    & td {
        border-top: 2px solid #e2e8f0;
    }
`;

const LinhaNf = styled.tr`
    cursor: pointer;

    &:hover {
        background-color: #f1f5f9;
    }
`;

const formatQuantidade = (value) => {
    const numValue = typeof value === "string" ? parseFloat(value) : value;

    if (value == null || isNaN(numValue)) return " - ";

    return numValue.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const ModalSelecionarNf = ({ fecharModal, detalhes, loading, onVinculado }) => {
    const [nfAberta, setNfAberta] = useState(null);

    const pedido = detalhes?.pedido || null;
    const itens = detalhes?.itens || [];
    const nfs = detalhes?.nfs || [];

    const totalItens = itens.reduce((acc, item) => acc + (Number(item.VALOR_TOTAL) || 0), 0);

    const handleVinculado = () => {
        onVinculado?.();
        setNfAberta(null);
        fecharModal();
    };

    return (
        <>
            <Overlay state={true}>
                <Modal size="lg" header close={fecharModal}>
                    <Title>Selecionar NF para o Pedido</Title>

                    {loading ? (
                        <LoadingWrapper>
                            <SmallLoading text="Carregando informações do pedido..." />
                        </LoadingWrapper>
                    ) : !pedido ? (
                        <EmptyText>Não foi possível carregar os dados do pedido.</EmptyText>
                    ) : (
                        <>
                            <InfoGrid>
                                <InfoItem>
                                    <InfoLabel>Filial</InfoLabel>
                                    <InfoValue>{pedido.FILIAL || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Número Pedido</InfoLabel>
                                    <InfoValue>{pedido.NUM_PEDIDO || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Data Emissão</InfoLabel>
                                    <InfoValue>{pedido.DT_EMISSAO || " - "}</InfoValue>
                                </InfoItem>
                                <InfoItem>
                                    <InfoLabel>Fornecedor</InfoLabel>
                                    <InfoValue>{pedido.FORNECEDOR || " - "}</InfoValue>
                                </InfoItem>
                            </InfoGrid>

                            <SectionTitle>Itens do Pedido</SectionTitle>
                            {itens.length === 0 ? (
                                <EmptyText>Nenhum item encontrado para este pedido.</EmptyText>
                            ) : (
                                <TableWrapper>
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                <th>COD. PRODUTO</th>
                                                <th>DESCRIÇÃO</th>
                                                <th>UNIDADE</th>
                                                <th>QUANTIDADE</th>
                                                <th>VALOR TOTAL</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {itens.map((item, index) => (
                                                <tr key={`${item.COD_PRODUTO}-${index}`}>
                                                    <td>{item.COD_PRODUTO || " - "}</td>
                                                    <td>{item.DESC_PRODUTO || " - "}</td>
                                                    <Coluna align="center">{item.UNIDADE || " - "}</Coluna>
                                                    <Coluna align="right">{formatQuantidade(item.QUANTIDADE)}</Coluna>
                                                    <Coluna align="right">
                                                        {item.VALOR_TOTAL != null
                                                            ? `R$ ${formatCurrency(item.VALOR_TOTAL)}`
                                                            : " - "}
                                                    </Coluna>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <TotalRow>
                                                <td colSpan={4}>Valor total do pedido</td>
                                                <Coluna align="right">{`R$ ${formatCurrency(totalItens)}`}</Coluna>
                                            </TotalRow>
                                        </tfoot>
                                    </DataTable>
                                </TableWrapper>
                            )}

                            <SectionTitle>
                                Notas Fiscais Disponíveis
                                <Hint>Clique em uma NF para ver os itens e analisar</Hint>
                            </SectionTitle>
                            {nfs.length === 0 ? (
                                <EmptyText>Nenhuma NF disponível para este fornecedor.</EmptyText>
                            ) : (
                                <TableWrapper>
                                    <DataTable>
                                        <thead>
                                            <tr>
                                                <th>NOTA</th>
                                                <th>DATA EMISSÃO</th>
                                                <th>VALOR BRUTO</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {nfs.map((nf, index) => (
                                                <LinhaNf
                                                    key={`${nf.ZBZ_NOTA}-${index}`}
                                                    onClick={() => setNfAberta(nf)}
                                                >
                                                    <td>{nf.ZBZ_NOTA || " - "}</td>
                                                    <Coluna align="center">{nf.ZBZ_DTNFE || " - "}</Coluna>
                                                    <Coluna align="right">
                                                        {nf.ZBZ_VLBRUT != null
                                                            ? `R$ ${formatCurrency(nf.ZBZ_VLBRUT)}`
                                                            : " - "}
                                                    </Coluna>
                                                </LinhaNf>
                                            ))}
                                        </tbody>
                                    </DataTable>
                                </TableWrapper>
                            )}
                        </>
                    )}
                </Modal>
            </Overlay>

            {nfAberta && (
                <ModalItensNf
                    pedido={pedido}
                    nf={nfAberta}
                    onClose={() => setNfAberta(null)}
                    onVinculado={handleVinculado}
                />
            )}
        </>
    );
};

export default ModalSelecionarNf;
